import type { PloscicaPregleda, TockaPregleda } from '@koda/shared';
import type { JSX, ReactNode } from 'react';

import { evri } from '../lib/format';
import { useBesedila } from '../lib/jezik';
import { useTelefon } from '../lib/telefon';
import { celicaDelov, sPredznakom } from './kazalniki-skupno';
import type { Celica } from './PanelRazclenitve';

interface LastnostiGlave {
  naslov: string;
  podnaslov: string;
  /** Dimenzija, primerjalno leto, izvoz in stikalo pogleda – v isti vrstici desno. */
  children: ReactNode;
}

/**
 * Glava strani Kazalniki (Pregled in Podrobno, točka 1 navodila): v eni
 * vrstici levo naslov in obdobje, desno filtri; filtri niso naloženi v
 * stolpec nad vsebino.
 */
export function GlavaKazalnikov({ naslov, podnaslov, children }: LastnostiGlave): JSX.Element {
  return (
    <header className="kz-glava">
      <div className="kz-naslov">
        <h1>{naslov}</h1>
        <div className="kz-podnaslov">{podnaslov}</div>
      </div>
      <div className="kz-kontrole">{children}</div>
    </header>
  );
}

/** Mere male krivulje ploščice po zasnovi (80 × 28, točke od 4 do 76, višina 3–25). */
const K = { sirina: 80, visina: 28, levo: 4, desno: 76, vrh: 3, dno: 25 } as const;

/**
 * Mala krivulja ploščice: letošnji meseci (pri denarju z začetkom leta), zadnja
 * točka poudarjena, ničelna črta samo, kadar krivulja seka nič.
 */
export function Iskrica({ vrednosti }: { vrednosti: string[] }): JSX.Element | null {
  if (vrednosti.length < 2) return null;
  const st = vrednosti.map(Number);
  const min = Math.min(...st);
  const max = Math.max(...st);
  const razpon = max - min || 1;
  const x = (i: number): number => K.levo + (i * (K.desno - K.levo)) / (st.length - 1);
  const y = (v: number): number => K.vrh + ((max - v) / razpon) * (K.dno - K.vrh);
  const pot = st.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const zadnja = st.length - 1;
  return (
    <svg className="kz-iskrica" width={K.sirina} height={K.visina} viewBox={`0 0 ${String(K.sirina)} ${String(K.visina)}`} aria-hidden="true">
      {min < 0 && max > 0 && <line x1="0" x2={K.sirina} y1={y(0).toFixed(1)} y2={y(0).toFixed(1)} stroke="#e9e7e1" strokeWidth="1" />}
      <path d={pot} fill="none" stroke="#256abf" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={x(zadnja).toFixed(1)} cy={y(st[zadnja]!).toFixed(1)} r="3.5" fill="#256abf" />
    </svg>
  );
}

/** Puščica smeri: gor zelena (boljši izid), dol rdeča (slabši). */
function Puscica({ gor }: { gor: boolean }): JSX.Element {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
      <path d={gor ? 'M6 2 L10.5 9 H1.5 Z' : 'M6 10 L10.5 3 H1.5 Z'} fill="currentColor" />
    </svg>
  );
}

/** Znesek s predznakom na evre: »+109.395 €«, »−17.925 €«. */
export function evriSPredznakom(z: string): string {
  return `${Number(z) > 0 ? '+' : ''}${evri(z)}`;
}

interface LastnostiPloscic {
  ploscice: PloscicaPregleda[];
  leto: number;
  naCelico: (celica: Celica) => void;
}

/**
 * Ploščice (točka 2): kratek naziv in mala krivulja, velika številka, puščica
 * z odstotkom (zelena boljši, rdeča slabši izid – pri ploščicah je višje
 * boljše), nato razlika in primerjalna vrednost. Pri denarju je primerjava
 * 1. 1. Številka in primerjava odpreta knjižbe. Na telefonu so štiri
 * (brez dodane vrednosti, ki je v Ustvarjanju vrednosti).
 */
export function PlosciceKazalnikov({ ploscice, leto, naCelico }: LastnostiPloscic): JSX.Element | null {
  const b = useBesedila();
  const telefon = useTelefon();
  const vidne = telefon ? ploscice.filter((p) => p.koda !== 'dodana_vrednost') : ploscice;
  if (vidne.length === 0) return null;
  return (
    <section aria-label={b.pk.kljucniZneski} className="kz-ploscice">
      {vidne.map((p) => {
        // Denar: krivulja začne s stanjem 1. 1., kot v zasnovi.
        const tocke: string[] = [
          ...(p.koda === 'denar' && p.primerjava !== null ? [p.primerjava] : []),
          ...p.poMesecih.t.map((t: TockaPregleda) => t.vrednost),
        ];
        const gor = p.razlika !== null && Number(p.razlika) >= 0;
        return (
          <article key={p.koda} className="kz-ploscica">
            <div className="kz-ploscica-vrh">
              <h3>{p.naziv}</h3>
              <Iskrica vrednosti={tocke} />
            </div>
            {p.vrednost === null ? (
              <p className="kz-ploscica-vrednost">{b.pk.ni}</p>
            ) : (
              <button
                type="button"
                className="kz-ploscica-vrednost"
                title={b.namigCelice}
                onClick={() => {
                  naCelico(celicaDelov(p.naziv, String(leto), p.vrednost!, p.deli));
                }}
              >
                {evri(p.vrednost)}
              </button>
            )}
            {p.razlika !== null && p.primerjava !== null && (
              <div className="kz-ploscica-sprememba">
                {p.odstotek !== null && (
                  <span className={`kz-smer ${gor ? 'boljse' : 'slabse'}`}>
                    <Puscica gor={gor} />
                    {sPredznakom(p.odstotek)} %
                  </span>
                )}
                <span className="kz-ploscica-lani">
                  {evriSPredznakom(p.razlika)} ·{' '}
                  <button
                    type="button"
                    className="kz-tiho-gumb"
                    title={b.namigCelice}
                    onClick={() => {
                      naCelico(celicaDelov(p.naziv, p.nazivPrimerjave, p.primerjava!, p.deliPrimerjave));
                    }}
                  >
                    {p.nazivPrimerjave}: {evri(p.primerjava)}
                  </button>
                </span>
              </div>
            )}
          </article>
        );
      })}
    </section>
  );
}
